import { CalendarMonth, CheckCircle, AccessTimeFilled, Cancel, Group, ExpandMore, SignalCellularAlt, CloudDone, AccessTime } from '@mui/icons-material';
import { Box, Stack, Typography, Chip, Accordion, AccordionSummary, AccordionDetails, Grid, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { MuiCard } from '@ace/ace-ui-web';
import StatCard from '../../components/statcard/StatCard';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchDashboardReport, DashboardDetail } from '../../redux/DashboardReportReducer/dashboardReportSlice';
import { getDefaultReportStartDate, getDefaultReportEndDate } from '../../utils/reportDateDefaults';
import { getShiftStatusAbbreviation } from '../../utils/statusChipConfig';
import useCustomNavigate from '../../hooks/useCustomNavigate';
import ReportDateRangeFilter from '../../components/common/ReportDateRangeFilter';
import { formatSkillCodes } from '../../utils/skillUtils';

// ========================================
// TYPES
// ========================================
interface Shift {
    id: string;
    shiftDate: string;
    weekDay: string;
    status: string;
    shiftType: string;
    worker: string;
    skill: string;
    team: string;
    startTime: string;
    endTime: string;
    workdayInterfaced: boolean;
    postedByUserId: string | null;
    postedByUserNm: string | null;
}

interface TeamSchedule {
    teamName: string;
    shiftNumber: string;
    shiftType: string;
    startTime: string;
    endTime: string;
    postedByUserId: string | null;
    postedByUserNm: string | null;
    workers: {
        id: string;
        worker: string;
        status: string;
        workdayInterfaced: boolean;
    }[];
}

interface DaySchedule {
    date: string;
    weekDay: string;
    teams: TeamSchedule[];
}

// ========================================
// HELPERS
// ========================================
const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
        'Approved': '#4caf50',
        'Pending Approval': '#ff9800',
        'Rejected': '#f44336',
    };
    return colors[status] || '#9e9e9e';
};

const calculatePercentage = (value: number, total: number): number => {
    if (!total) return 0;
    return +(value / total * 100).toFixed(1);
};

const getWeekDay = (requestDt: string | undefined | null): string => {
    if (!requestDt) return '';
    const normalized = requestDt.includes('T') ? requestDt : `${requestDt}T00:00:00`;
    return new Date(normalized).toLocaleDateString('en-US', { weekday: 'long' });
};

const mapToShifts = (data: DashboardDetail[], includeCancelled: boolean): Shift[] =>
    data.filter(item => {
        if (!includeCancelled && item.requestStatus === 'Cancelled') return false;
        if (item.requestType === 'GIG' && item.requestStatus === 'Denied') return false;
        return true;
    })
        .map((item) => ({
            id: `${item.requestId}-${item.requestSeqId}`,
            shiftDate: item.requestDt ? item.requestDt.split('T')[0] : '',
            weekDay: getWeekDay(item.requestDt),
            status: item.requestStatus || '',
            shiftType: item.requestType || '',
            worker: item.claimedByUserNm || '',
            skill: formatSkillCodes(item.requiredSkill),
            team: item.requiredTeam || '',
            startTime: item.startTime || '',
            endTime: item.endTime || '',
            workdayInterfaced: item.syncedToWorkday,
            postedByUserId: item.postedByUserId || null,
            postedByUserNm: item.postedByUserNm || null,
        }));

// Fix #3 — extracted + simplified to reduce cognitive complexity
const groupShiftsByDay = (shifts: Shift[]): DaySchedule[] => {
    const dateMap = new Map<string, Shift[]>();

    for (const shift of shifts) {
        // Fix #2 — no non-null assertion; use null-coalescing initialisation
        const existing = dateMap.get(shift.shiftDate) ?? [];
        dateMap.set(shift.shiftDate, [...existing, shift]);
    }

    const daySchedules: DaySchedule[] = [];

    dateMap.forEach((dayShifts, date) => {
        const teamMap = new Map<string, Shift[]>();

        for (const shift of dayShifts) {
            const teamKey = `${shift.skill}__${shift.team}__${shift.shiftType}__${shift.startTime}__${shift.endTime}`;
            const existing = teamMap.get(teamKey) ?? [];
            teamMap.set(teamKey, [...existing, shift]);
        }

        const teams: TeamSchedule[] = [];

        teamMap.forEach((teamShifts, teamKey) => {
            // Fix #5 — guard against empty array before accessing index 0
            if (teamShifts.length === 0) return;

            const parts = teamKey.split('__');
            const skillName = parts[0] ?? '';
            const teamName = parts[1] ?? '';
            const first = teamShifts[0];

            teams.push({
                teamName: skillName,
                shiftNumber: teamName,
                shiftType: first.shiftType,
                startTime: first.startTime,
                endTime: first.endTime,
                postedByUserId: first.postedByUserId,
                postedByUserNm: first.postedByUserNm,
                workers: teamShifts.map((s) => ({
                    id: s.id,
                    worker: s.worker,
                    status: s.status,
                    workdayInterfaced: s.workdayInterfaced,
                })),
            });
        });

        teams.sort((a, b) =>
            a.teamName.localeCompare(b.teamName) || a.shiftNumber.localeCompare(b.shiftNumber)
        );

        daySchedules.push({ date, weekDay: dayShifts[0]?.weekDay ?? '', teams });
    });

    return daySchedules.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

// Returns MUI Grid column span based on container width rather than viewport width
const getStatCardColumns = (containerWidth: number): number => {
    if (containerWidth >= 800) return 3;  // 4 per row
    if (containerWidth >= 500) return 6;  // 2 per row
    return 12;                            // 1 per row
};

// ========================================
// COMPONENT
// ========================================
const DashboardReportView: React.FC = () => {
    const dispatch = useAppDispatch();
    const { navigateWithParams } = useCustomNavigate();
    const { user: currentUser } = useAppSelector((state) => state.currentUser);
    const { dashboardData, rejectedShifts, loading, error } = useAppSelector((state) => state.dashboardReport);

    const [fromDate, setFromDate] = useState<string>(getDefaultReportStartDate());
    const [toDate, setToDate] = useState<string>(getDefaultReportEndDate());
    const [expandedDay, setExpandedDay] = useState<string | false>(false);
    const [containerWidth, setContainerWidth] = useState<number>(800);

    const containerRef = useRef<HTMLDivElement>(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Observe the dashboard container's width so stat card columns
    // reflow based on available space rather than the viewport width.
    // This ensures the sidebar being expanded is taken into account.
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) {
                setContainerWidth(entry.contentRect.width);
            }
        });

        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (currentUser?.siteCd) {
            dispatch(fetchDashboardReport({
                siteCd: currentUser.siteCd,
                startDate: fromDate || undefined,
                endDate: toDate || undefined,
            }));
        }
    }, [dispatch, currentUser, fromDate, toDate]);

    useEffect(() => {
        if (error) {
            toast.error(`Failed to load dashboard report: ${error}`);
        }
    }, [error]);

    // Fix #4 — removed unused `_` parameter; event argument is simply omitted
    const handleAccordionChange = (date: string) => (isExpanded: boolean) => {
        setExpandedDay(isExpanded ? date : false);
    };

    // Navigation handlers for stat cards
    const handlePendingClick = () => {
        navigateWithParams('/approve-reject', { state: { selectedSupervisorId: 'all' } });
    };

    const handleWorkdaySyncClick = () => {
        navigateWithParams('/reports/workday-sync', { state: { fromWorkdaySyncCard: true } });
    };

    const dashboardMetricShifts: Shift[] = mapToShifts(dashboardData, false);
    const allShifts: Shift[] = mapToShifts(dashboardData, true);
    const rejectedCount = rejectedShifts.length;

    const statusCounts = {
        approved: dashboardMetricShifts.filter(s => s.status === 'Approved').length,
        pending: dashboardMetricShifts.filter(s => s.status === 'Pending Approval').length,
        rejected: rejectedCount,
    };

    const workdaySynced = dashboardMetricShifts.filter(s => s.workdayInterfaced).length;
    const approvedSynced = dashboardMetricShifts.filter(s => s.status === 'Approved' && s.workdayInterfaced).length;
    const totalVtoAndSwapShifts = dashboardMetricShifts.filter(s => s.shiftType !== 'SFT' && s.shiftType !== 'SFT-D' && s.status === 'Approved').length;

    const shiftTypeBreakdown = {
        asg: dashboardMetricShifts.filter(s => s.shiftType === 'SFT').length,
        vto: dashboardMetricShifts.filter(s => s.shiftType === 'VTO').length,
        swap: dashboardMetricShifts.filter(s => s.shiftType === 'WKR').length,
        can: allShifts.filter(s => s.status === 'Cancelled').length,
    };

    // Fix #3 — memoized so it only recalculates when dashboardMetricShifts change
    const daySchedules = useMemo(() => groupShiftsByDay(dashboardMetricShifts), [dashboardMetricShifts]);

    const statCardCols = getStatCardColumns(containerWidth);

    return (
        <Box ref={containerRef} sx={{ width: '100%', p: { xs: 1, sm: 2 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* Summary Stat Cards */}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: statCardCols }}>
                    <StatCard
                        value={dashboardMetricShifts.length}
                        title="Total Upcoming Shifts"
                        note="Excluding Cancelled and Rejected"
                        IconProp={<CalendarMonth />}
                        colorProp="lightblue"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: statCardCols }}>
                    <StatCard
                        value={calculatePercentage(statusCounts.approved, allShifts.length)}
                        isPercentage={true}
                        title="Fill Rate"
                        note={`${statusCounts.approved} approved of ${allShifts.length} posted shifts (includes Cancelled)`}
                        IconProp={<CheckCircle />}
                        colorProp="green"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: statCardCols }}>
                    <StatCard
                        value={statusCounts.pending}
                        title="Pending Approval"
                        note="Awaiting review"
                        IconProp={<AccessTimeFilled />}
                        colorProp="yellow"
                        onClick={handlePendingClick}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: statCardCols }}>
                    <StatCard
                        value={workdaySynced}
                        title="Workday Synced"
                        note={`${calculatePercentage(workdaySynced, totalVtoAndSwapShifts)}% of VTO & Swap shifts`}
                        IconProp={<CloudDone />}
                        colorProp="blue"
                        onClick={handleWorkdaySyncClick}
                    />
                </Grid>
            </Grid>

            {/* Team Schedules by Day */}
            <MuiCard borderRadius={2} elevation={5} padding={0} variant="elevation" sx={{ width: '100%' }}>
                <Stack spacing={2} sx={{ p: 2 }}>
                    <Stack spacing={0.5}>
                        <Typography variant="h6">Team Schedules by Day</Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                            Click on any day to see detailed team assignments
                        </Typography>
                    </Stack>

                    {/* Date Filters */}
                    <ReportDateRangeFilter
                        fromDate={fromDate}
                        toDate={toDate}
                        onFromDateChange={setFromDate}
                        onToDateChange={setToDate}
                        fromLabelClassName="form-label"
                        toLabelClassName="form-label"
                    />

                    {/* Loading State / Accordion Day Rows */}
                    {loading ? (
                        <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ minHeight: '200px' }}>
                            <CircularProgress />
                            <Typography variant="body1">Loading report data...</Typography>
                        </Stack>
                    ) : (
                        <Stack spacing={1}>
                            {daySchedules.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4, color: '#999' }}>
                                    <Typography variant="body1">No shifts found for the selected date range</Typography>
                                </Box>
                            ) : (
                                daySchedules.map((daySchedule) => (
                                    <Accordion
                                        key={daySchedule.date}
                                        expanded={expandedDay === daySchedule.date}
                                        onChange={(_, isExpanded) => handleAccordionChange(daySchedule.date)(isExpanded)}
                                        sx={{
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '6px !important',
                                            boxShadow: 'none',
                                            '&:before': { display: 'none' },
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMore />}
                                            sx={{
                                                borderRadius: '6px',
                                                '&.Mui-expanded': { borderBottom: '1px solid #e0e0e0' },
                                            }}
                                        >
                                            <Stack
                                                direction={{ xs: 'column', sm: 'row' }}
                                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                                justifyContent="space-between"
                                                spacing={{ xs: 0.75, sm: 0 }}
                                                sx={{ width: '100%', pr: 1 }}
                                            >
                                                <Stack>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                        {daySchedule.weekDay}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: '#666' }}>
                                                        {daySchedule.date}
                                                    </Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={1}>
                                                    <Chip
                                                        label={`${daySchedule.teams.length} ${daySchedule.teams.length === 1 ? 'Request' : 'Requests'}`}
                                                        size="small"
                                                        sx={{ backgroundColor: '#e0e0e0', fontWeight: 600 }}
                                                    />
                                                    <Chip
                                                        label={`${daySchedule.teams.reduce((sum, t) => sum + t.workers.length, 0)} Workers`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </Stack>
                                            </Stack>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 2 }}>
                                            <Stack spacing={1.5}>
                                                {/* Fix #1 — stable key from team data instead of array index */}
                                                {daySchedule.teams.map((team) => (
                                                    <MuiCard
                                                        key={`${team.teamName}__${team.shiftNumber}__${team.shiftType}__${team.startTime}`}
                                                        borderRadius={2}
                                                        elevation={0}
                                                        padding={0}
                                                        variant="outlined"
                                                        sx={{ borderLeft: '4px solid #CC0000', width: '100%' }}
                                                    >
                                                        <Stack spacing={1} sx={{ p: 1.5 }}>

                                                            {/* Team header */}
                                                            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                                                                <Group sx={{ fontSize: 18, color: '#CC0000' }} />
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                    {team.teamName}{team.shiftNumber ? ` — ${team.shiftNumber}` : ''}
                                                                </Typography>
                                                                {team.shiftType === 'WKR' && (
                                                                    <Typography variant="body2" sx={{ color: '#666' }}>
                                                                        Posted By: {team.postedByUserNm}
                                                                    </Typography>
                                                                )}
                                                                <Chip
                                                                    label={team.shiftType}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ fontWeight: 700, backgroundColor: '#f5f5f5', color: '#444', borderColor: '#ccc' }}
                                                                />
                                                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                                                    <AccessTime sx={{ fontSize: 14, color: '#666' }} />
                                                                    <Typography variant="caption" sx={{ color: '#666' }}>
                                                                        {team.startTime} — {team.endTime}
                                                                    </Typography>
                                                                </Stack>
                                                            </Stack>

                                                            {/* Worker rows */}
                                                            <Stack spacing={0.75}>
                                                                {team.workers.map((worker) => (
                                                                    <Stack
                                                                        key={worker.id}
                                                                        direction="row"
                                                                        alignItems="center"
                                                                        justifyContent="space-between"
                                                                        flexWrap="wrap"
                                                                        gap={1}
                                                                        sx={{ p: '10px 12px', borderRadius: '6px', backgroundColor: '#fafafa', border: '1px solid #eeeeee' }}
                                                                    >
                                                                        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: '80px' }}>
                                                                            {worker.worker}
                                                                        </Typography>
                                                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                                            {worker.workdayInterfaced && (
                                                                                <Chip
                                                                                    label="✓ Workday"
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                    sx={{ backgroundColor: '#e3f2fd', color: '#1565c0', borderColor: '#90caf9', fontSize: '0.7rem' }}
                                                                                />
                                                                            )}
                                                                            <Chip
                                                                                label={isMobile ? getShiftStatusAbbreviation(worker.status) : worker.status}
                                                                                size="small"
                                                                                sx={{
                                                                                    backgroundColor: getStatusColor(worker.status),
                                                                                    color: 'white',
                                                                                    fontWeight: 600,
                                                                                    fontSize: '0.75rem',
                                                                                }}
                                                                            />
                                                                        </Stack>
                                                                    </Stack>
                                                                ))}
                                                            </Stack>
                                                        </Stack>
                                                    </MuiCard>
                                                ))}
                                            </Stack>
                                        </AccordionDetails>
                                    </Accordion>
                                ))
                            )}
                        </Stack>
                    )}
                </Stack>
            </MuiCard>

            {/* Bottom Breakdown Cards */}
            <Grid container spacing={2}>
                {/* Status Breakdown */}
                <Grid size={{ xs: 12, lg: 6 }}>
                    <MuiCard borderRadius={2} elevation={5} padding={0} variant="elevation" sx={{ width: '100%', height: '100%' }}>
                        <Stack spacing={1.5} sx={{ p: 2 }}>
                            <Stack spacing={0}>
                                <Typography variant="h6">Status Breakdown</Typography>
                                <Typography variant="caption" sx={{ color: '#666' }}>
                                    (includes Gig Worker Requests)
                                </Typography>
                            </Stack>
                            <Stack
                                spacing={0.75}
                                sx={{ p: '12px 16px', borderRadius: '6px', backgroundColor: '#e8f5e9', border: '1px solid #c8e6c9' }}
                            >
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                        <CheckCircle sx={{ color: '#388e3c', fontSize: 24 }} />
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>Approved</Typography>
                                    </Stack>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#388e3c' }}>
                                        {statusCounts.approved}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="flex-end">
                                    <Chip
                                        label={`${approvedSynced} synced to Workday`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ backgroundColor: '#e3f2fd', color: '#1565c0', borderColor: '#90caf9', fontSize: '0.7rem' }}
                                    />
                                </Stack>
                            </Stack>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ p: '12px 16px', borderRadius: '6px', backgroundColor: '#fff8e1', border: '1px solid #ffe082' }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <AccessTimeFilled sx={{ color: '#f9a825', fontSize: 24 }} />
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>Pending Approval</Typography>
                                </Stack>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: '#f9a825' }}>
                                    {statusCounts.pending}
                                </Typography>
                            </Stack>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ p: '12px 16px', borderRadius: '6px', backgroundColor: '#ffebee', border: '1px solid #ffcdd2' }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <Cancel sx={{ color: '#d32f2f', fontSize: 24 }} />
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>Rejected</Typography>
                                </Stack>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                                    {statusCounts.rejected}
                                </Typography>
                            </Stack>
                        </Stack>
                    </MuiCard>
                </Grid>

                {/* Shift Type Distribution */}
                <Grid size={{ xs: 12, lg: 6 }}>
                    <MuiCard borderRadius={2} elevation={5} padding={0} variant="elevation" sx={{ width: '100%', height: '100%' }}>
                        <Stack spacing={1.5} sx={{ p: 2 }}>
                            <Typography variant="h6">Shift Type Distribution</Typography>
                            <StatCard
                                value={shiftTypeBreakdown.asg}
                                title="Shift"
                                note="Not including expired/cancelled"
                                IconProp={<SignalCellularAlt />}
                                colorProp="red"
                                variant="shifttype"
                                percentage={calculatePercentage(shiftTypeBreakdown.asg, dashboardMetricShifts.length)}
                            />
                            <StatCard
                                value={shiftTypeBreakdown.vto}
                                title="VTO"
                                note="Not including expired/cancelled"
                                IconProp={<SignalCellularAlt />}
                                colorProp="blue"
                                variant="shifttype"
                                percentage={calculatePercentage(shiftTypeBreakdown.vto, dashboardMetricShifts.length)}
                            />
                            <StatCard
                                value={shiftTypeBreakdown.swap}
                                title="Swap"
                                note="Not including expired/cancelled"
                                IconProp={<SignalCellularAlt />}
                                colorProp="yellow"
                                variant="shifttype"
                                percentage={calculatePercentage(shiftTypeBreakdown.swap, dashboardMetricShifts.length)}
                            />
                            <StatCard
                                value={shiftTypeBreakdown.can}
                                title="Expired/Cancelled"
                                note="Includes all shift types"
                                IconProp={<Cancel />}
                                colorProp="gray"
                                variant="shifttype"
                            />
                        </Stack>
                    </MuiCard>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DashboardReportView;